import { uuidModule } from "@/modules/uuid";
import { AppStore } from "@/store/AppStore";
import {
  SpaceAccountCollectionModelData,
  SpaceAccountCollectionUpsertedSyncUpdateValue,
} from "@/store/recent-items/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IMarkCollectionViewedOperation } from "@/store/sync/operations/types";
import {
  SyncCustomErrorData,
  SyncModelData,
  SyncOperationKind,
  OptimisticSyncUpdate,
} from "@/store/sync/types";

export class MarkCollectionViewedOperation extends BaseSyncOperation<IMarkCollectionViewedOperation> {
  get operationKind(): SyncOperationKind {
    return "MARK_COLLECTION_VIEWED";
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    return MarkCollectionViewedOperation.generateOptimisticUpdatesFor({
      store: this.store,
      collectionId: this.payload.collection_id,
      committedAt: this.committedAt,
    });
  }

  public static generateOptimisticUpdatesFor({
    store,
    collectionId,
    committedAt,
  }: {
    store: AppStore;
    collectionId: string;
    committedAt: string;
  }): OptimisticSyncUpdate<SyncModelData>[] {
    const spaceAccountId = store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountCollectionId = uuidModule.resolveSpaceAccountCollectionSyncModelUuid({
      spaceAccountId,
      collectionId,
    });
    const spaceAccountCollectionObservable =
      store.spaceAccountCollections.get(spaceAccountCollectionId);

    const value: SpaceAccountCollectionUpsertedSyncUpdateValue = {
      model_id: spaceAccountCollectionId,
      model_kind: "SPACE_ACCOUNT_COLLECTION",
      model_version: spaceAccountCollectionObservable?.modelVersion ?? 0,
      model_data: {
        space_account_id: spaceAccountId,
        collection_id: collectionId,
        last_viewed_at: committedAt,
        last_added_to_at: spaceAccountCollectionObservable?.modelData.last_added_to_at || null,
        last_removed_from_at:
          spaceAccountCollectionObservable?.modelData.last_removed_from_at || null,
        shared_with_me_at: spaceAccountCollectionObservable?.modelData.shared_with_me_at || null,
        shared_with_me_by_space_account_id:
          spaceAccountCollectionObservable?.modelData.shared_with_me_by_space_account_id || null,
      },
      model_scopes: spaceAccountCollectionObservable
        ? spaceAccountCollectionObservable.modelScopes
        : [generateDefaultOwnerScopes({ store })],
    };
    const syncUpdate: OptimisticSyncUpdate<SpaceAccountCollectionModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: committedAt,
      kind: "UPSERTED",
      value,
    };
    return [syncUpdate];
  }

  async triggerRecompute() {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountCollectionId = uuidModule.resolveSpaceAccountCollectionSyncModelUuid({
      spaceAccountId,
      collectionId: this.payload.collection_id,
    });
    await this.store.spaceAccountCollections.recompute(spaceAccountCollectionId);
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.ignoreError(SyncErrorHandlingType.RetryWithLimit);
  }
}
